import { useEffect, useRef, useState } from 'react'
import { AtSign, ChevronDown, ChevronRight, File, Folder, FolderPlus, MoreHorizontal, PanelLeftClose, Upload } from 'lucide-react'
import { Menu } from '@/components/ui'
import { Button } from '@/components/shadcn/button'
import { ask, askText } from '@/components/ask'
import { cn } from '@/lib/cn'
import type { BoxSocketState } from '@/lib/box'
import { FileEditor } from './FileEditor'
import { DRAG_PATH, FilesClient, joinPath, mention, useTermInsert, type FileEntry, type FilesReady } from '@/lib/files'

/**
 * The session's folder, next to its terminal: browse it, upload into
 * `uploads/` (git ignores it, so docs never land in the PR), download,
 * rename, delete, open a file in the editor, and drop a file into the
 * session as `@path`.
 */
export function FilePanel({ sessionId, mode, onHide }: { sessionId: string; mode: 'ro' | 'rw'; onHide: () => void }) {
  const client = useRef<FilesClient | null>(null)
  const [ready, setReady] = useState<FilesReady | null>(null)
  const [state, setState] = useState<BoxSocketState>('connecting')
  const [dirs, setDirs] = useState<Record<string, FileEntry[] | null>>({})
  const [open, setOpen] = useState<Set<string>>(new Set(['']))
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [editing, setEditing] = useState<{ path: string; bytes: number; mtime: number } | null>(null)
  const picker = useRef<HTMLInputElement>(null)
  const pickInto = useRef('uploads')
  const insert = useTermInsert((s) => s.insert)

  useEffect(() => {
    setDirs({}); setReady(null); setOpen(new Set(['']))
    const c = new FilesClient(sessionId, mode, {
      ready: (r) => {
        setReady(r)
        // The uploads folder is where docs go; show it open from the start.
        setOpen((o) => new Set([...o, r.uploads]))
        c.watch(r.uploads)
      },
      listing: (path, entries) => setDirs((d) => ({ ...d, [path]: entries })),
      state: setState,
    })
    client.current = c
    c.watch('')
    return () => { c.stop(); client.current = null }
  }, [sessionId, mode])

  const writable = !!ready?.writable
  const uploads = ready?.uploads ?? 'uploads'
  const inUploads = (p: string) => p === uploads || p.startsWith(`${uploads}/`)

  const run = async (label: string, f: () => Promise<unknown>) => {
    setError(null); setBusy(label)
    try { await f() } catch (e) { setError((e as Error).message) } finally { setBusy(null) }
  }

  const toggle = (path: string) => {
    const c = client.current
    setOpen((o) => {
      const n = new Set(o)
      if (n.has(path)) { n.delete(path); c?.forget(path) } else { n.add(path); c?.watch(path) }
      return n
    })
  }

  const uploadFiles = (files: FileList | File[], dir: string) => {
    const into = inUploads(dir) ? dir : uploads
    const list = [...files]
    const tooBig = list.find((f) => ready && f.size > ready.max_upload)
    if (tooBig) { setError(`${tooBig.name} is over 100 MB`); return }
    void run('Uploading', async () => {
      for (const f of list) {
        await client.current!.upload(joinPath(into, f.name), f, (done) => setBusy(`Uploading ${f.name} · ${Math.round((done / Math.max(f.size, 1)) * 100)}%`))
      }
      if (!open.has(into)) toggle(into)
    })
  }

  const download = (path: string, name: string) => void run(`Downloading ${name}`, async () => {
    const blob = await client.current!.download(path)
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob); a.download = name; a.click()
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000)
  })

  const remove = (path: string, e: FileEntry) => void ask({
    title: `Delete ${e.name}?`, body: e.dir ? 'The folder and everything in it are deleted from the session.' : 'The file is deleted from the session.', confirm: 'Delete', danger: true,
  }).then((ok) => { if (ok) void run('Deleting', () => client.current!.remove(path)) })

  const rename = (path: string, e: FileEntry) => void askText({ title: `Rename ${e.name}`, confirm: 'Rename', value: e.name }).then((name) => {
    if (!name || name === e.name) return
    const parent = path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : ''
    void run('Renaming', () => client.current!.rename(path, joinPath(parent, name)))
  })

  const newFolder = (dir: string) => void askText({ title: 'New folder', confirm: 'Create' }).then((name) => {
    if (!name) return
    void run('Creating', async () => { await client.current!.mkdir(joinPath(dir, name)); if (!open.has(dir)) toggle(dir) })
  })

  const reference = (path: string) => insert(sessionId, mention(path))

  const row = (dir: string, depth: number) => {
    const entries = dirs[dir]
    if (entries === undefined) return <div className="py-0.5 text-fg-faint" style={{ paddingLeft: 8 + depth * 12 }}>Loading…</div>
    if (entries === null) return <div className="py-0.5 text-fg-faint" style={{ paddingLeft: 8 + depth * 12 }}>Not there any more</div>
    if (!entries.length) return <div className="py-0.5 text-fg-faint" style={{ paddingLeft: 8 + depth * 12 }}>{dir === uploads ? 'Drop files here' : 'Empty'}</div>
    return entries.map((e) => {
      const path = joinPath(dir, e.name)
      const isOpen = open.has(path)
      const items = [
        ...(!e.dir ? [{ label: writable ? 'Edit' : 'View', onSelect: () => setEditing({ path, bytes: e.bytes, mtime: e.mtime_ms }) }] : []),
        { label: 'Reference in session', onSelect: () => reference(path) },
        ...(!e.dir ? [{ label: 'Download', onSelect: () => download(path, e.name) }] : []),
        ...(e.dir && writable && inUploads(path) ? [{ label: 'Upload here', onSelect: () => { pickInto.current = path; picker.current?.click() } }] : []),
        ...(e.dir && writable ? [{ label: 'New folder inside', onSelect: () => newFolder(path) }] : []),
        ...(writable && e.name !== '.git' ? ['sep' as const, { label: 'Rename', onSelect: () => rename(path, e) }, { label: 'Delete', onSelect: () => remove(path, e), danger: true }] : []),
      ]
      return (
        <div key={path}>
          <div draggable onDragStart={(ev) => { ev.dataTransfer.setData(DRAG_PATH, path); ev.dataTransfer.setData('text/plain', mention(path)); ev.dataTransfer.effectAllowed = 'copy' }}
            onDragOver={(ev) => { if (e.dir && writable && inUploads(path) && ev.dataTransfer.types.includes('Files')) { ev.preventDefault(); ev.stopPropagation() } }}
            onDrop={(ev) => { if (e.dir && ev.dataTransfer.files.length) { ev.preventDefault(); ev.stopPropagation(); setDragOver(false); uploadFiles(ev.dataTransfer.files, path) } }}
            className="group flex h-6 cursor-pointer items-center gap-1 rounded-xs pr-1 hover:bg-hover"
            style={{ paddingLeft: 4 + depth * 12 }}
            title={e.dir ? path : `${path} · ${size(e.bytes)} · click to open it`}
            onClick={() => e.dir ? toggle(path) : setEditing({ path, bytes: e.bytes, mtime: e.mtime_ms })}>
            {e.dir ? (isOpen ? <ChevronDown size={12} className="shrink-0 text-fg-faint" /> : <ChevronRight size={12} className="shrink-0 text-fg-faint" />) : <span className="w-3 shrink-0" />}
            {e.dir ? <Folder size={13} className="shrink-0 text-fg-subtle" /> : <File size={13} className="shrink-0 text-fg-faint" />}
            <span className={cn('min-w-0 flex-1 truncate', e.name.startsWith('.') && 'text-fg-faint')}>{e.name}</span>
            {path === uploads && <span className="text-[10px] text-fg-faint" title="Git ignores this folder: uploads never go into a commit">not in git</span>}
            {!e.dir && <span className="hidden text-[10px] text-fg-faint group-hover:inline">{size(e.bytes)}</span>}
            <Button type="button" variant="ghost" size="icon-xs" aria-label={`Reference ${e.name} in the session`} title="Reference in session (@path)"
              className="hidden size-5 rounded-xs text-fg-subtle hover:bg-hover hover:text-fg group-hover:inline-flex"
              onClick={(ev) => { ev.stopPropagation(); reference(path) }}><AtSign size={11} /></Button>
            <Menu trigger={<Button type="button" variant="ghost" size="icon-xs" aria-label={`Actions for ${e.name}`} className="hidden size-5 rounded-xs text-fg-subtle hover:bg-hover hover:text-fg group-hover:inline-flex data-[state=open]:inline-flex" onClick={(ev) => ev.stopPropagation()}><MoreHorizontal size={12} /></Button>} items={items} />
          </div>
          {e.dir && isOpen && row(path, depth + 1)}
        </div>
      )
    })
  }

  return (
    <aside className={cn('flex h-full min-h-0 flex-col bg-surface-1 text-xs', dragOver && 'ring-1 ring-inset ring-accent')}
      onDragOver={(e) => { if (writable && e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDragOver(true) } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false) }}
      onDrop={(e) => { if (e.dataTransfer.files.length) { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files, uploads) } }}>
      <div className="flex h-7 shrink-0 items-center gap-1 border-b border-border px-2 text-fg-subtle">
        <span className="font-medium text-fg-muted">Files</span>
        <span className={cn('size-1.5 rounded-full', state === 'open' ? 'bg-ok' : state === 'unreachable' || state === 'ended' ? 'bg-err' : 'bg-warn')} title={state} />
        <span className="ml-auto" />
        {writable && <>
          <Button type="button" variant="ghost" size="icon-xs" title={`Upload into ${uploads}/`} aria-label="Upload files" className="size-5 rounded-xs hover:bg-hover"
            onClick={() => { pickInto.current = uploads; picker.current?.click() }}><Upload size={13} /></Button>
          <Button type="button" variant="ghost" size="icon-xs" title="New folder" aria-label="New folder" className="size-5 rounded-xs hover:bg-hover" onClick={() => newFolder('')}><FolderPlus size={13} /></Button>
        </>}
        <Button type="button" variant="ghost" size="icon-xs" title="Hide files" aria-label="Hide files" className="size-5 rounded-xs hover:bg-hover" onClick={onHide}><PanelLeftClose size={13} /></Button>
      </div>
      <input ref={picker} type="file" multiple className="hidden" onChange={(e) => { if (e.target.files) uploadFiles(e.target.files, pickInto.current); e.target.value = '' }} />
      {ready && <div className="truncate border-b border-border px-2 py-1 font-mono text-[10px] text-fg-faint" title={ready.root}>{ready.root}</div>}
      <div className="min-h-0 flex-1 overflow-auto py-1 pl-1">{row('', 0)}</div>
      {(busy || error) && <div role={error ? 'alert' : 'status'} className={cn('border-t border-border px-2 py-1', error ? 'text-err' : 'text-fg-subtle')}>{error ?? busy}</div>}
      {editing && client.current && <FileEditor client={client.current} {...editing} writable={writable} onClose={() => setEditing(null)} />}
      {writable && <div className="border-t border-border px-2 py-1 text-[10px] text-fg-faint">Drop files here or on the terminal to upload into {uploads}/ · drag a file onto the terminal to reference it</div>}
    </aside>
  )
}

function size(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}
